import {
  ArrowLeftIcon,
  ArrowRightIcon,
  Button,
  DatePicker,
  EmptyState,
  TimetableIcon,
  TimetableView,
  TimetableViewEntryType,
} from '@bp/ui-components';
import styles from './Timetable.module.scss';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import classNames from 'classnames';
import { useTimetable } from 'hooks/useTimetable';

export type TimetableDay = { date: Dayjs; dayOfWeek: string; entries: TimetableViewEntryType[] };

type TimetableProps = {
  showDaySelect?: boolean;
  showLegend?: boolean;
  showHeader?: boolean;
  hideTodayHighlight?: boolean;
  days?: 1 | 5 | 6 | 7;
  onEventClick?: (uuid: string) => void;
};

export const Timetable = ({
  showDaySelect,
  showLegend,
  showHeader,
  hideTodayHighlight,
  days = 5,
  onEventClick,
}: TimetableProps) => {
  const { t } = useTranslation();

  const { getTimetableStartEnd, getTimetableDays } = useTimetable();

  const [selectedDay, setSelectedDay] = useState<Dayjs>(dayjs());

  const { start, end } = getTimetableStartEnd();
  const timetableDays: TimetableDay[] = getTimetableDays(selectedDay, days, onEventClick);

  return (
    <div className={styles.timetable}>
      {showDaySelect && (
        <div className={styles.actions}>
          <div className={styles.date}>
            <Button
              name='decrease'
              hierarchy='tertiary'
              onClick={() => setSelectedDay(selectedDay.subtract(1, 'day'))}
              icon={<ArrowLeftIcon />}
            />
            <DatePicker
              className={styles.picker}
              onChange={(e) => setSelectedDay(dayjs(e))}
              name='date'
              value={selectedDay.toDate()}
              dense
            />
            <Button
              name='increase'
              hierarchy='tertiary'
              onClick={() => setSelectedDay(selectedDay.add(1, 'day'))}
              icon={<ArrowRightIcon />}
            />
          </div>
          <Button
            className={styles.today}
            hierarchy='secondary'
            onClick={() => setSelectedDay(dayjs())}
            disabled={selectedDay.isSame(dayjs(), 'day')}
          >
            {t('common.today')}
          </Button>
        </div>
      )}

      <div className={styles['timetable-views']}>
        {timetableDays.length === 0 ? (
          <EmptyState title={t('timetable.noTimetableDays')} icon={<TimetableIcon />} forcedHeight='330px' />
        ) : (
          <>
            {showLegend && (
              <TimetableView
                className={classNames(styles.legend, { [styles.spacing]: showHeader })}
                start={start.toDate()}
                end={end.toDate()}
                entries={[]}
                showTimegrid
              />
            )}
            {timetableDays.map(({ date, dayOfWeek, entries }) => {
              return (
                <div
                  key={date.toISOString()}
                  className={classNames(styles.day, {
                    [styles.active]: !hideTodayHighlight && date.isSame(dayjs(), 'day'),
                  })}
                  style={{ flexBasis: days === 1 ? 'auto' : `${100 / 5}%` }}
                >
                  {showHeader && (
                    <div className={styles['day-header']}>
                      <div className={styles.name}>{dayOfWeek}</div>
                      <div className={styles.date}>{date.format('DD. MMMM YY')}</div>
                    </div>
                  )}
                  <TimetableView start={start.toDate()} end={end.toDate()} entries={entries} />
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
};
